import { useState, useEffect } from 'react';
import { User, Loader, TrendingUp, Award, PieChart } from 'lucide-react';
import { getDynamicProfile } from '../../services/recommendations';

export default function ProfilDynamiquePanel() {
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);

  const loadProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      // Récupérer l'utilisateur connecté depuis localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.id) {
        setError('Utilisateur non connecté');
        return;
      }

      const result = await getDynamicProfile(user.id);
      setProfile(result);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du chargement du profil');
      console.error('Erreur profil:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
          <User className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Profil Dynamique</h2>
          <p className="text-sm text-gray-600">Votre empreinte de compétences</p>
        </div>
      </div>

      {/* Bouton */}
      <button
        onClick={loadProfile}
        disabled={loading}
        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50 mb-6"
      >
        {loading ? (
          <>
            <Loader className="w-5 h-5 animate-spin" />
            Chargement...
          </>
        ) : (
          <>
            <TrendingUp className="w-5 h-5" />
            Charger mon profil
          </>
        )}
      </button>

      {/* Erreur */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Profil */}
      {profile && (
        <div className="space-y-6">
          {/* Stats globales */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-blue-700">{profile.stats.totalCompetences}</div>
              <div className="text-xs text-blue-600 mt-1">Compétences</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-green-700">{profile.stats.totalProjects}</div>
              <div className="text-xs text-green-600 mt-1">Projets</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-purple-700">
                {Object.keys(profile.stats.byCategory).length}
              </div>
              <div className="text-xs text-purple-600 mt-1">Catégories</div>
            </div>
          </div>

          {/* Info utilisateur */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <User className="w-5 h-5 text-gray-400" />
              <span className="font-semibold text-gray-900">{profile.userName}</span>
            </div>
            <div className="flex gap-4 text-sm">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                {profile.level || 'Non spécifié'}
              </span>
              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full">
                {profile.sector || 'Tous secteurs'}
              </span>
            </div>
          </div>

          {/* Top compétences */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Award className="w-5 h-5 text-yellow-600" />
              <h3 className="font-semibold text-gray-900">Top 10 Compétences</h3>
            </div>
            <div className="space-y-2">
              {profile.stats.mostFrequent.map((comp, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-700 font-bold text-sm">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900">{comp.label}</span>
                      <span className="text-sm text-gray-500">{comp.count} projet{comp.count > 1 ? 's' : ''}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-indigo-500 to-purple-500"
                        style={{ width: `${(comp.count / profile.stats.totalProjects) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Par catégorie */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <PieChart className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-gray-900">Répartition par catégorie</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(profile.stats.byCategory).map(([category, count], index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3">
                  <div className="font-medium text-gray-900 mb-1">{category}</div>
                  <div className="text-2xl font-bold text-indigo-600">{count}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {!loading && !profile && !error && (
        <div className="text-center py-12 text-gray-500">
          <User className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p>Cliquez sur le bouton pour visualiser votre profil dynamique</p>
        </div>
      )}
    </div>
  );
}